import React, { useState, useEffect } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  User,
  Bell,
  Shield,
  Palette,
  Eye,
  EyeOff,
  Save,
  Settings as SettingsIcon,
  Loader,
  Trash2,
} from "lucide-react";

const SettingsPage = ({ user, userRole }) => {
  const { themeClasses, theme, setTheme } = useTheme();
  const [activeSection, setActiveSection] = useState("profile");
  const [showEmail, setShowEmail] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Initialize Firebase Functions
  const functions = getFunctions();

  // Form states
  const [profileData, setProfileData] = useState({
    displayName: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    dateOfBirth: "",
  });

  const [addressData, setAddressData] = useState({
    street: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
  });

  const [notifications, setNotifications] = useState({
    petAlerts: true,
    healthReminders: true,
    systemUpdates: false,
    marketing: false,
  });

  const [privacySettings, setPrivacySettings] = useState({
    profileVisibility: "public",
    shareLocation: false,
    shareContactInfo: false,
    allowPetMatching: true,
  });

  const [preferences, setPreferences] = useState({
    theme: "dark",
    language: "en",
    timezone: "UTC",
    units: "metric",
    currency: "USD",
  });

  const [emergencyContact, setEmergencyContact] = useState({
    name: "",
    relationship: "",
    phoneNumber: "",
    email: "",
  });

  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const fileInputRef = React.useRef(null);

  // Load user profile on component mount
  useEffect(() => {
    if (user) {
      loadUserProfile();
    }
  }, [user]);

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      const getUserProfile = httpsCallable(functions, "getUserProfile");
      const result = await getUserProfile();

      if (result.data.success) {
        const profile = result.data.data;
        setUserProfile(profile);

        // Populate form states with loaded data
        setProfileData({
          displayName: profile.displayName || "",
          firstName: profile.firstName || "",
          lastName: profile.lastName || "",
          phoneNumber: profile.phoneNumber || "",
          dateOfBirth: profile.dateOfBirth || "",
        });

        setAddressData({
          street: profile.address?.street || "",
          city: profile.address?.city || "",
          state: profile.address?.state || "",
          zipCode: profile.address?.zipCode || "",
          country: profile.address?.country || "",
        });

        console.log("Loaded address data:", {
          street: profile.address?.street || "",
          city: profile.address?.city || "",
          state: profile.address?.state || "",
          zipCode: profile.address?.zipCode || "",
          country: profile.address?.country || "",
        });

        setNotifications({
          petAlerts: profile.notifications?.petAlerts !== false,
          healthReminders: profile.notifications?.healthReminders !== false,
          systemUpdates: profile.notifications?.systemUpdates || false,
          marketing: profile.notifications?.marketing || false,
        });

        setPrivacySettings({
          profileVisibility: profile.privacy?.profileVisibility || "public",
          shareLocation: profile.privacy?.shareLocation || false,
          shareContactInfo: profile.privacy?.shareContactInfo || false,
          allowPetMatching: profile.privacy?.allowPetMatching !== false,
        });

        setPreferences({
          theme: profile.preferences?.theme || "dark",
          language: profile.preferences?.language || "en",
          timezone: profile.preferences?.timezone || "UTC",
          units: profile.preferences?.units || "metric",
          currency: profile.preferences?.currency || "USD",
        });

        setEmergencyContact({
          name: profile.emergencyContact?.name || "",
          relationship: profile.emergencyContact?.relationship || "",
          phoneNumber: profile.emergencyContact?.phoneNumber || "",
          email: profile.emergencyContact?.email || "",
        });

        console.log("User profile loaded successfully:", profile);
      }
    } catch (error) {
      console.error("Failed to load user profile:", error);
      alert("Failed to load profile data. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  // Split display name into first and last name
  const splitDisplayName = (displayName) => {
    if (!displayName) return { firstName: "", lastName: "" };
    const nameParts = displayName.trim().split(" ");
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";
    return { firstName, lastName };
  };

  const sections = [
    { id: "profile", name: "Profile", icon: User },
    { id: "notifications", name: "Notifications", icon: Bell },
    { id: "privacy", name: "Privacy", icon: Shield },
    { id: "appearance", name: "Appearance", icon: Palette },
  ];

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file (JPG, PNG, or GIF)");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert("File size must be less than 5MB");
      return;
    }

    setIsUploadingPhoto(true);

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const photoURL = e.target.result;

          // Update profile with new photo
          const updateUserProfile = httpsCallable(
            functions,
            "updateUserProfile"
          );
          await updateUserProfile({
            updates: { photoURL },
          });

          // Update local state
          setUserProfile((prev) => ({ ...prev, photoURL }));

          alert("Profile photo updated successfully!");
        } catch (error) {
          console.error("Error updating photo:", error);
          alert("Failed to update photo. Please try again.");
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error uploading photo:", error);
      alert("Failed to upload photo. Please try again.");
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = async () => {
    try {
      const updateUserProfile = httpsCallable(functions, "updateUserProfile");
      await updateUserProfile({
        updates: { photoURL: "" },
      });

      // Update local state
      setUserProfile((prev) => ({ ...prev, photoURL: "" }));

      alert("Profile photo removed successfully!");
    } catch (error) {
      console.error("Error removing photo:", error);
      alert("Failed to remove photo. Please try again.");
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);

      const updates = {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        phoneNumber: profileData.phoneNumber,
        dateOfBirth: profileData.dateOfBirth,
        displayName: profileData.displayName,
        address: addressData,
        notifications: notifications,
        privacy: privacySettings,
        preferences: preferences,
        emergencyContact: emergencyContact,
      };

      console.log("Sending updates to Firebase:", updates);
      console.log("Emergency contact data:", emergencyContact);

      const updateUserProfile = httpsCallable(functions, "updateUserProfile");
      const result = await updateUserProfile({ updates });

      console.log("Firebase function response:", result.data);

      if (result.data.success) {
        // Update local state
        setUserProfile((prev) => ({ ...prev, ...updates }));
        alert("Settings saved successfully!");
        console.log("Updated fields:", result.data.updatedFields);
      } else {
        throw new Error(result.data.message || "Failed to save settings");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      console.error("Full error object:", error);
      alert("Failed to save settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmEmail !== user?.email) {
      alert("Email confirmation does not match your account email.");
      return;
    }

    if (
      !window.confirm(
        "Are you absolutely sure you want to delete your account? This action cannot be undone and will permanently delete all your data including pets, health records, and API keys."
      )
    ) {
      return;
    }

    try {
      setDeleting(true);

      const deleteUserAccount = httpsCallable(functions, "deleteUserAccount");
      const result = await deleteUserAccount({
        confirmEmail: deleteConfirmEmail,
      });

      if (result.data.success) {
        alert(
          "Account deleted successfully. You will be redirected to the login page."
        );
        // The user will be automatically signed out by the function
        window.location.href = "/";
      } else {
        throw new Error(result.data.message || "Failed to delete account");
      }
    } catch (error) {
      console.error("Error deleting account:", error);
      alert("Failed to delete account. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <Loader
            className={`w-8 h-8 animate-spin mx-auto mb-4 ${themeClasses.text.accent}`}
          />
          <p className={themeClasses.text.secondary}>
            Loading your settings...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1
            className={`text-2xl font-bold flex items-center ${themeClasses.text.primary}`}
          >
            <SettingsIcon className="w-6 h-6 mr-2 text-cyan-400" />
            Settings
          </h1>
          <p className={`mt-1 ${themeClasses.text.secondary}`}>
            Manage your account preferences and privacy settings
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div
            className={`${themeClasses.bg.card} rounded-xl p-4 border ${themeClasses.border.primary}`}
          >
            <nav className="space-y-2">
              {sections.map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                      activeSection === section.id
                        ? `${themeClasses.bg.button} text-white`
                        : `${themeClasses.text.secondary} ${themeClasses.cardHover}`
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{section.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          <div
            className={`${themeClasses.bg.card} rounded-xl p-6 border ${themeClasses.border.primary}`}
          >
            {/* Profile Section */}
            {activeSection === "profile" && (
              <div className="space-y-6">
                <h2
                  className={`text-xl font-semibold mb-4 ${themeClasses.text.primary}`}
                >
                  Profile Information
                </h2>

                {/* Profile Avatar Section */}
                <div
                  className={`p-6 rounded-lg ${themeClasses.bg.secondary} ${themeClasses.border.primary} border`}
                >
                  <h3
                    className={`text-lg font-medium mb-4 ${themeClasses.text.primary}`}
                  >
                    Profile Photo
                  </h3>
                  <div className="flex items-center space-x-6">
                    <div className="relative">
                      <div className="w-24 h-24 rounded-full overflow-hidden bg-gradient-to-br from-cyan-500 to-purple-600 p-0.5">
                        <div
                          className={`w-full h-full rounded-full ${themeClasses.bg.secondary} flex items-center justify-center`}
                        >
                          {userProfile?.photoURL ? (
                            <img
                              src={userProfile.photoURL}
                              alt="Profile"
                              className="w-full h-full rounded-full object-cover"
                            />
                          ) : (
                            <User
                              className={`w-10 h-10 ${themeClasses.text.secondary}`}
                            />
                          )}
                        </div>
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-cyan-500 rounded-full flex items-center justify-center cursor-pointer hover:bg-cyan-400 transition-colors">
                        <svg
                          className="w-4 h-4 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                          />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-1">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handlePhotoUpload}
                        accept="image/*"
                        className="hidden"
                      />
                      <div className="flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isUploadingPhoto}
                          className={`${themeClasses.bg.button} text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity flex items-center space-x-2 disabled:opacity-50`}
                        >
                          {isUploadingPhoto ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                          )}
                          <span>
                            {isUploadingPhoto ? "Uploading..." : "Upload Photo"}
                          </span>
                        </button>
                        <button
                          onClick={handleRemovePhoto}
                          className={`${themeClasses.bg.buttonSecondary} ${themeClasses.text.primary} px-4 py-2 rounded-lg border ${themeClasses.border.primary} hover:opacity-90 transition-opacity flex items-center space-x-2`}
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                          <span>Remove</span>
                        </button>
                      </div>
                      <p
                        className={`text-sm mt-2 ${themeClasses.text.secondary}`}
                      >
                        JPG, PNG or GIF. Max size 5MB. Recommended 400x400px.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label
                      className={`block text-sm font-medium mb-2 ${themeClasses.text.secondary}`}
                    >
                      Display Name
                    </label>
                    <input
                      type="text"
                      value={profileData.displayName}
                      onChange={(e) => {
                        const newDisplayName = e.target.value;
                        const { firstName, lastName } =
                          splitDisplayName(newDisplayName);
                        setProfileData((prev) => ({
                          ...prev,
                          displayName: newDisplayName,
                          firstName,
                          lastName,
                        }));
                      }}
                      placeholder="Enter display name"
                      className={`w-full px-3 py-2 rounded-lg ${themeClasses.bg.input} ${themeClasses.border.primary} border ${themeClasses.text.primary} focus:ring-2 focus:ring-cyan-500 focus:border-transparent`}
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${themeClasses.text.secondary}`}
                    >
                      First Name
                    </label>
                    <input
                      type="text"
                      value={profileData.firstName}
                      onChange={(e) =>
                        setProfileData((prev) => ({
                          ...prev,
                          firstName: e.target.value,
                        }))
                      }
                      placeholder="Enter first name"
                      className={`w-full px-3 py-2 rounded-lg ${themeClasses.bg.input} ${themeClasses.border.primary} border ${themeClasses.text.primary} focus:ring-2 focus:ring-cyan-500 focus:border-transparent`}
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${themeClasses.text.secondary}`}
                    >
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={profileData.lastName}
                      onChange={(e) =>
                        setProfileData((prev) => ({
                          ...prev,
                          lastName: e.target.value,
                        }))
                      }
                      placeholder="Enter last name"
                      className={`w-full px-3 py-2 rounded-lg ${themeClasses.bg.input} ${themeClasses.border.primary} border ${themeClasses.text.primary} focus:ring-2 focus:ring-cyan-500 focus:border-transparent`}
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${themeClasses.text.secondary}`}
                    >
                      Email Address
                    </label>
                    <div className="relative">
                      <input
                        type={showEmail ? "email" : "password"}
                        value={user?.email || ""}
                        readOnly
                        className={`w-full px-3 py-2 pr-10 rounded-lg ${themeClasses.bg.input} ${themeClasses.border.primary} border ${themeClasses.text.primary}`}
                      />
                      <button
                        onClick={() => setShowEmail(!showEmail)}
                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${themeClasses.text.secondary} hover:${themeClasses.text.primary}`}
                      >
                        {showEmail ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${themeClasses.text.secondary}`}
                    >
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={profileData.phoneNumber}
                      onChange={(e) =>
                        setProfileData((prev) => ({
                          ...prev,
                          phoneNumber: e.target.value,
                        }))
                      }
                      placeholder="Enter phone number"
                      className={`w-full px-3 py-2 rounded-lg ${themeClasses.bg.input} ${themeClasses.border.primary} border ${themeClasses.text.primary} focus:ring-2 focus:ring-cyan-500 focus:border-transparent`}
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${themeClasses.text.secondary}`}
                    >
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      value={profileData.dateOfBirth}
                      onChange={(e) =>
                        setProfileData((prev) => ({
                          ...prev,
                          dateOfBirth: e.target.value,
                        }))
                      }
                      className={`w-full px-3 py-2 rounded-lg ${themeClasses.bg.input} ${themeClasses.border.primary} border ${themeClasses.text.primary} focus:ring-2 focus:ring-cyan-500 focus:border-transparent`}
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${themeClasses.text.secondary}`}
                    >
                      Account Type
                    </label>
                    <div
                      className={`px-3 py-2 rounded-lg ${themeClasses.bg.input} ${themeClasses.border.primary} border`}
                    >
                      <span
                        className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                          userRole === "admin"
                            ? "bg-purple-600 text-white"
                            : userRole === "pro"
                            ? "bg-cyan-600 text-white"
                            : `${themeClasses.bg.tertiary} ${themeClasses.text.secondary}`
                        }`}
                      >
                        {userRole?.toUpperCase() || "FREE"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Address Information */}
                <div className={`pt-6 border-t ${themeClasses.border.primary}`}>
                  <h3
                    className={`text-lg font-medium mb-4 ${themeClasses.text.primary}`}
                  >
                    Address Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label
                        className={`block text-sm font-medium mb-2 ${themeClasses.text.secondary}`}
                      >
                        Street Address
                      </label>
                      <input
                        type="text"
                        value={addressData.street}
                        onChange={(e) =>
                          setAddressData((prev) => ({
                            ...prev,
                            street: e.target.value,
                          }))
                        }
                        placeholder="Enter street address"
                        className={`w-full px-3 py-2 rounded-lg ${themeClasses.bg.input} ${themeClasses.border.primary} border ${themeClasses.text.primary} focus:ring-2 focus:ring-cyan-500 focus:border-transparent`}
                      />
                    </div>
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${themeClasses.text.secondary}`}
                      >
                        City
                      </label>
                      <input
                        type="text"
                        value={addressData.city}
                        onChange={(e) =>
                          setAddressData((prev) => ({
                            ...prev,
                            city: e.target.value,
                          }))
                        }
                        placeholder="Enter city"
                        className={`w-full px-3 py-2 rounded-lg ${themeClasses.bg.input} ${themeClasses.border.primary} border ${themeClasses.text.primary} focus:ring-2 focus:ring-cyan-500 focus:border-transparent`}
                      />
                    </div>
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${themeClasses.text.secondary}`}
                      >
                        State/Province
                      </label>
                      <input
                        type="text"
                        value={addressData.state}
                        onChange={(e) =>
                          setAddressData((prev) => ({
                            ...prev,
                            state: e.target.value,
                          }))
                        }
                        placeholder="Enter state/province"
                        className={`w-full px-3 py-2 rounded-lg ${themeClasses.bg.input} ${themeClasses.border.primary} border ${themeClasses.text.primary} focus:ring-2 focus:ring-cyan-500 focus:border-transparent`}
                      />
                    </div>
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${themeClasses.text.secondary}`}
                      >
                        ZIP/Postal Code
                      </label>
                      <input
                        type="text"
                        value={addressData.zipCode}
                        onChange={(e) =>
                          setAddressData((prev) => ({
                            ...prev,
                            zipCode: e.target.value,
                          }))
                        }
                        placeholder="Enter ZIP/postal code"
                        className={`w-full px-3 py-2 rounded-lg ${themeClasses.bg.input} ${themeClasses.border.primary} border ${themeClasses.text.primary} focus:ring-2 focus:ring-cyan-500 focus:border-transparent`}
                      />
                    </div>
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${themeClasses.text.secondary}`}
                      >
                        Country
                      </label>
                      <select
                        value={addressData.country}
                        onChange={(e) =>
                          setAddressData((prev) => ({
                            ...prev,
                            country: e.target.value,
                          }))
                        }
                        className={`w-full px-3 py-2 rounded-lg ${themeClasses.bg.input} ${themeClasses.border.primary} border ${themeClasses.text.primary} focus:ring-2 focus:ring-cyan-500 focus:border-transparent`}
                      >
                        <option value="">Select country</option>
                        <option value="United States">United States</option>
                        <option value="Canada">Canada</option>
                        <option value="United Kingdom">United Kingdom</option>
                        <option value="Australia">Australia</option>
                        <option value="Germany">Germany</option>
                        <option value="France">France</option>
                        <option value="Japan">Japan</option>
                        <option value="Norway">Norway</option>
                        <option value="Norge">Norge</option>
                        <option value="Sweden">Sweden</option>
                        <option value="Danmark">Danmark</option>
                        <option value="Denmark">Denmark</option>
                        <option value="Iceland">Iceland</option>
                        <option value="Ísland">Ísland</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Emergency Contact */}
                <div className={`pt-6 border-t ${themeClasses.border.primary}`}>
                  <h3
                    className={`text-lg font-medium mb-4 ${themeClasses.text.primary}`}
                  >
                    Emergency Contact
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${themeClasses.text.secondary}`}
                      >
                        Contact Name
                      </label>
                      <input
                        type="text"
                        value={emergencyContact.name}
                        onChange={(e) =>
                          setEmergencyContact((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        placeholder="Enter emergency contact name"
                        className={`w-full px-3 py-2 rounded-lg ${themeClasses.bg.input} ${themeClasses.border.primary} border ${themeClasses.text.primary} focus:ring-2 focus:ring-cyan-500 focus:border-transparent`}
                      />
                    </div>
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${themeClasses.text.secondary}`}
                      >
                        Relationship
                      </label>
                      <select
                        value={emergencyContact.relationship}
                        onChange={(e) =>
                          setEmergencyContact((prev) => ({
                            ...prev,
                            relationship: e.target.value,
                          }))
                        }
                        className={`w-full px-3 py-2 rounded-lg ${themeClasses.bg.input} ${themeClasses.border.primary} border ${themeClasses.text.primary} focus:ring-2 focus:ring-cyan-500 focus:border-transparent`}
                      >
                        <option value="">Select relationship</option>
                        <option value="spouse">Spouse</option>
                        <option value="parent">Parent</option>
                        <option value="sibling">Sibling</option>
                        <option value="child">Child</option>
                        <option value="friend">Friend</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${themeClasses.text.secondary}`}
                      >
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={emergencyContact.phoneNumber}
                        onChange={(e) =>
                          setEmergencyContact((prev) => ({
                            ...prev,
                            phoneNumber: e.target.value,
                          }))
                        }
                        placeholder="Enter emergency contact phone"
                        className={`w-full px-3 py-2 rounded-lg ${themeClasses.bg.input} ${themeClasses.border.primary} border ${themeClasses.text.primary} focus:ring-2 focus:ring-cyan-500 focus:border-transparent`}
                      />
                    </div>
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${themeClasses.text.secondary}`}
                      >
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={emergencyContact.email}
                        onChange={(e) =>
                          setEmergencyContact((prev) => ({
                            ...prev,
                            email: e.target.value,
                          }))
                        }
                        placeholder="Enter emergency contact email"
                        className={`w-full px-3 py-2 rounded-lg ${themeClasses.bg.input} ${themeClasses.border.primary} border ${themeClasses.text.primary} focus:ring-2 focus:ring-cyan-500 focus:border-transparent`}
                      />
                    </div>
                  </div>
                </div>

                {/* App Preferences */}
                <div className={`pt-6 border-t ${themeClasses.border.primary}`}>
                  <h3
                    className={`text-lg font-medium mb-4 ${themeClasses.text.primary}`}
                  >
                    App Preferences
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${themeClasses.text.secondary}`}
                      >
                        Language
                      </label>
                      <select
                        value={preferences.language}
                        onChange={(e) =>
                          setPreferences((prev) => ({
                            ...prev,
                            language: e.target.value,
                          }))
                        }
                        className={`w-full px-3 py-2 rounded-lg ${themeClasses.bg.input} ${themeClasses.border.primary} border ${themeClasses.text.primary} focus:ring-2 focus:ring-cyan-500 focus:border-transparent`}
                      >
                        <option value="en">English</option>
                        <option value="es">Español</option>
                        <option value="fr">Français</option>
                        <option value="de">Deutsch</option>
                        <option value="ja">日本語</option>
                      </select>
                    </div>
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${themeClasses.text.secondary}`}
                      >
                        Units
                      </label>
                      <select
                        value={preferences.units}
                        onChange={(e) =>
                          setPreferences((prev) => ({
                            ...prev,
                            units: e.target.value,
                          }))
                        }
                        className={`w-full px-3 py-2 rounded-lg ${themeClasses.bg.input} ${themeClasses.border.primary} border ${themeClasses.text.primary} focus:ring-2 focus:ring-cyan-500 focus:border-transparent`}
                      >
                        <option value="metric">Metric (kg, cm)</option>
                        <option value="imperial">Imperial (lbs, in)</option>
                      </select>
                    </div>
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${themeClasses.text.secondary}`}
                      >
                        Currency
                      </label>
                      <select
                        value={preferences.currency}
                        onChange={(e) =>
                          setPreferences((prev) => ({
                            ...prev,
                            currency: e.target.value,
                          }))
                        }
                        className={`w-full px-3 py-2 rounded-lg ${themeClasses.bg.input} ${themeClasses.border.primary} border ${themeClasses.text.primary} focus:ring-2 focus:ring-cyan-500 focus:border-transparent`}
                      >
                        <option value="USD">USD ($)</option>
                        <option value="EUR">EUR (€)</option>
                        <option value="GBP">GBP (£)</option>
                        <option value="CAD">CAD ($)</option>
                        <option value="AUD">AUD ($)</option>
                        <option value="JPY">JPY (¥)</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Notifications Section */}
            {activeSection === "notifications" && (
              <div className="space-y-6">
                <h2
                  className={`text-xl font-semibold mb-4 ${themeClasses.text.primary}`}
                >
                  Notification Preferences
                </h2>

                <div className="space-y-4">
                  {Object.entries(notifications).map(([key, value]) => (
                    <div
                      key={key}
                      className={`flex items-center justify-between p-4 rounded-lg ${themeClasses.bg.secondary} ${themeClasses.border.primary} border`}
                    >
                      <div>
                        <h3
                          className={`font-medium ${themeClasses.text.primary}`}
                        >
                          {key === "petAlerts" && "Pet Alerts"}
                          {key === "healthReminders" && "Health Reminders"}
                          {key === "systemUpdates" && "System Updates"}
                          {key === "marketing" && "Marketing Communications"}
                        </h3>
                        <p className={`text-sm ${themeClasses.text.secondary}`}>
                          {key === "petAlerts" &&
                            "Get notified about your pets' activities and alerts"}
                          {key === "healthReminders" &&
                            "Receive reminders for vet appointments and health checks"}
                          {key === "systemUpdates" &&
                            "Stay informed about new features and updates"}
                          {key === "marketing" &&
                            "Receive promotional emails and special offers"}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={(e) =>
                            setNotifications((prev) => ({
                              ...prev,
                              [key]: e.target.checked,
                            }))
                          }
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-600"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Privacy Section */}
            {activeSection === "privacy" && (
              <div className="space-y-6">
                <h2
                  className={`text-xl font-semibold mb-4 ${themeClasses.text.primary}`}
                >
                  Privacy Settings
                </h2>

                <div className="space-y-4">
                  <div
                    className={`p-4 rounded-lg ${themeClasses.bg.secondary} ${themeClasses.border.primary} border`}
                  >
                    <h3
                      className={`font-medium mb-2 ${themeClasses.text.primary}`}
                    >
                      Profile Visibility
                    </h3>
                    <p
                      className={`text-sm mb-3 ${themeClasses.text.secondary}`}
                    >
                      Control who can see your profile information
                    </p>
                    <select
                      value={privacySettings.profileVisibility}
                      onChange={(e) =>
                        setPrivacySettings((prev) => ({
                          ...prev,
                          profileVisibility: e.target.value,
                        }))
                      }
                      className={`w-full px-3 py-2 rounded-lg ${themeClasses.bg.input} ${themeClasses.border.primary} border ${themeClasses.text.primary}`}
                    >
                      <option value="public">Public</option>
                      <option value="friends">Friends Only</option>
                      <option value="private">Private</option>
                    </select>
                  </div>

                  <div
                    className={`p-4 rounded-lg ${themeClasses.bg.secondary} ${themeClasses.border.primary} border`}
                  >
                    <h3
                      className={`font-medium mb-2 ${themeClasses.text.primary}`}
                    >
                      Data Sharing
                    </h3>
                    <p
                      className={`text-sm mb-3 ${themeClasses.text.secondary}`}
                    >
                      Allow anonymous usage data to help improve our services
                    </p>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={privacySettings.shareLocation}
                        onChange={(e) =>
                          setPrivacySettings((prev) => ({
                            ...prev,
                            shareLocation: e.target.checked,
                          }))
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-600"></div>
                    </label>
                  </div>

                  {/* Delete Account Section */}
                  <div
                    className={`p-4 rounded-lg border-2 border-red-500/50 bg-red-500/10`}
                  >
                    <h3 className={`font-medium mb-2 text-red-400`}>
                      Delete Account
                    </h3>
                    <p
                      className={`text-sm mb-4 ${themeClasses.text.secondary}`}
                    >
                      Permanently delete your account and all associated data.
                      This action cannot be undone.
                    </p>

                    {!showDeleteConfirm ? (
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Delete Account</span>
                      </button>
                    ) : (
                      <div className="space-y-4">
                        <div>
                          <label
                            className={`block text-sm font-medium mb-2 ${themeClasses.text.secondary}`}
                          >
                            Confirm your email address to delete account:
                          </label>
                          <input
                            type="email"
                            value={deleteConfirmEmail}
                            onChange={(e) =>
                              setDeleteConfirmEmail(e.target.value)
                            }
                            placeholder={user?.email || "Enter your email"}
                            className={`w-full px-3 py-2 rounded-lg ${themeClasses.bg.input} ${themeClasses.border.primary} border ${themeClasses.text.primary} focus:ring-2 focus:ring-red-500 focus:border-transparent`}
                          />
                        </div>
                        <div className="flex space-x-3">
                          <button
                            onClick={handleDeleteAccount}
                            disabled={
                              deleting || deleteConfirmEmail !== user?.email
                            }
                            className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {deleting ? (
                              <Loader className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                            <span>
                              {deleting ? "Deleting..." : "Confirm Delete"}
                            </span>
                          </button>
                          <button
                            onClick={() => {
                              setShowDeleteConfirm(false);
                              setDeleteConfirmEmail("");
                            }}
                            className={`px-4 py-2 ${themeClasses.bg.buttonSecondary} ${themeClasses.text.primary} border ${themeClasses.border.primary} rounded-lg hover:opacity-90 transition-opacity`}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Appearance Section */}
            {activeSection === "appearance" && (
              <div className="space-y-6">
                <h2
                  className={`text-xl font-semibold mb-4 ${themeClasses.text.primary}`}
                >
                  Appearance Settings
                </h2>

                <div
                  className={`p-4 rounded-lg ${themeClasses.bg.secondary} ${themeClasses.border.primary} border`}
                >
                  <h3
                    className={`font-medium mb-2 ${themeClasses.text.primary}`}
                  >
                    Theme Preference
                  </h3>
                  <p className={`text-sm mb-3 ${themeClasses.text.secondary}`}>
                    Choose your preferred color scheme
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setTheme("dark")}
                      className={`p-3 rounded-lg border-2 transition-colors ${
                        theme === "dark"
                          ? "border-cyan-500 bg-gray-900 text-white"
                          : `${themeClasses.border.primary} ${themeClasses.bg.input} ${themeClasses.text.primary}`
                      }`}
                    >
                      🌙 Dark
                    </button>
                    <button
                      onClick={() => setTheme("light")}
                      className={`p-3 rounded-lg border-2 transition-colors ${
                        theme === "light"
                          ? "border-cyan-500 bg-white text-gray-900"
                          : `${themeClasses.border.primary} ${themeClasses.bg.input} ${themeClasses.text.primary}`
                      }`}
                    >
                      ☀️ Light
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div
              className={`flex justify-end items-center pt-6 mt-6 border-t ${themeClasses.border.primary}`}
            >
              <button
                onClick={saveSettings}
                disabled={saving}
                className="flex items-center space-x-2 px-6 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                <span>{saving ? "Saving..." : "Save Changes"}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
